import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { formatPrice } from '../lib/api';

/**
 * RelatedContent — "You may also be interested in" internal linking block.
 * Renders a grid of small cards for Projects, Machines, or Services.
 *
 * Usage:
 *   <RelatedContent title="Related projects" items={projects} kind="project" />
 *   <RelatedContent title="Recommended equipment" items={machines} kind="machine" />
 *   <RelatedContent title="Services" items={services} kind="service" />
 */
const RelatedContent = ({ title = 'You may also be interested in', eyebrow = '/ Related', items = [], kind = 'project', className = '', testid }) => {
    if (!items || items.length === 0) return null;

    return (
        <section className={`border-t border-neutral-200 bg-neutral-50 ${className}`} data-testid={testid || `related-${kind}`}>
            <div className="container-page py-16 md:py-20">
                <div className="mb-8">
                    <div className="label-caps text-brand-primary mb-3 text-xs">{eyebrow}</div>
                    <h2 className="font-display font-black uppercase text-2xl md:text-4xl text-neutral-900 leading-tight">
                        {title}
                    </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.slice(0, 6).map((it) => {
                        if (kind === 'machine') {
                            return (
                                <Link
                                    key={it.id}
                                    to={`/machines/${it.slug || it.id}`}
                                    className="group flex flex-col bg-white border border-neutral-200 hover:border-brand-primary transition-colors"
                                    data-testid={`related-machine-${it.slug || it.id}`}
                                >
                                    <div className="aspect-[4/3] bg-neutral-50 overflow-hidden flex items-center justify-center">
                                        {it.image_url ? (
                                            <img
                                                src={it.image_url}
                                                alt={it.name}
                                                loading="lazy"
                                                decoding="async"
                                                className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                                            />
                                        ) : null}
                                    </div>
                                    <div className="p-5 flex-1 flex flex-col">
                                        <div className="label-caps text-neutral-500 text-xs mb-2">
                                            {(it.category || '').replace(/-/g, ' ')}
                                        </div>
                                        <div className="font-display font-bold uppercase text-lg text-neutral-900 leading-tight group-hover:text-brand-primary transition-colors">
                                            {it.name}
                                        </div>
                                        <div className="mt-3 text-sm">
                                            {it.contact_for_price ? (
                                                <span className="label-caps text-neutral-500 border border-neutral-300 px-2 py-1 inline-block text-xs">
                                                    Contact for pricing
                                                </span>
                                            ) : (
                                                <span className="font-display font-black text-lg text-brand-primary">
                                                    {formatPrice(it.price)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            );
                        }
                        if (kind === 'project') {
                            const cover = it.cover_image || it.images?.[0] || '';
                            return (
                                <Link
                                    key={it.id}
                                    to={`/projects/${it.slug || it.id}`}
                                    className="group flex flex-col bg-white border border-neutral-200 hover:border-brand-primary transition-colors"
                                    data-testid={`related-project-${it.slug || it.id}`}
                                >
                                    <div className="aspect-[4/3] bg-neutral-100 overflow-hidden">
                                        {cover ? (
                                            <img
                                                src={cover}
                                                alt={it.title}
                                                loading="lazy"
                                                decoding="async"
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-neutral-400 label-caps text-xs">No image</div>
                                        )}
                                    </div>
                                    <div className="p-5 flex-1 flex flex-col">
                                        <div className="label-caps text-neutral-500 text-xs mb-2">
                                            {(it.category || '').replace(/-/g, ' ')}
                                        </div>
                                        <div className="font-display font-bold uppercase text-lg text-neutral-900 leading-tight group-hover:text-brand-primary transition-colors">
                                            {it.title}
                                        </div>
                                        {it.summary && (
                                            <p className="mt-2 text-sm text-neutral-600 leading-relaxed line-clamp-2 flex-1">
                                                {it.summary}
                                            </p>
                                        )}
                                        <div className="mt-3 inline-flex items-center gap-1.5 label-caps text-brand-primary text-xs">
                                            View case study <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                                        </div>
                                    </div>
                                </Link>
                            );
                        }
                        // service
                        return (
                            <Link
                                key={it.key || it.slug || it.id}
                                to={it.href || `/services#${it.key || it.slug}`}
                                className="group flex flex-col bg-white border border-neutral-200 hover:border-brand-primary transition-colors p-6"
                                data-testid={`related-service-${it.key || it.slug || it.id}`}
                            >
                                <div className="label-caps text-neutral-500 text-xs mb-3">/ Service</div>
                                <div className="font-display font-bold uppercase text-xl text-neutral-900 leading-tight group-hover:text-brand-primary transition-colors">
                                    {it.title || it.name}
                                </div>
                                {it.blurb && (
                                    <p className="mt-3 text-sm text-neutral-600 leading-relaxed line-clamp-3 flex-1">
                                        {it.blurb}
                                    </p>
                                )}
                                <div className="mt-4 inline-flex items-center gap-1.5 label-caps text-brand-primary text-xs">
                                    Learn more <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default RelatedContent;
